import { DOCUMENT } from '@angular/common';
import {
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { debounce } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { WindowRefService } from 'i2g-ng-utils';
import { filter, takeUntil } from 'rxjs/operators';

export enum FillToBottomActionsEnum {
  resize = 'resize',
}

@Directive({
  selector: '[fillToBottom]',
})
export class FillToBottomDirective implements OnInit, OnDestroy {
  @Input() offsetFromBottom: number;
  @Input() fillToBottomActionTriggers$: Observable<FillToBottomActionsEnum>;

  @Output() onResize: EventEmitter<void> = new EventEmitter<void>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _el: HTMLElement;
  private _windowRef: Window;
  private _body: HTMLElement;
  private _html: HTMLElement;

  constructor(
    private _elementRef: ElementRef,
    private _windowRefService: WindowRefService,
    @Inject(DOCUMENT) private _document
  ) {
    this._body = this._document.body;
    this._html = this._document.documentElement;
  }

  ngOnInit() {
    this._el = this._elementRef.nativeElement;
    this._windowRef = this._windowRefService.nativeWindow;

    this.resize();

    if (this.fillToBottomActionTriggers$) {
      this.fillToBottomActionTriggers$
        .pipe(
          takeUntil(this._destroyed),
          filter(
            (actionTrigger: FillToBottomActionsEnum) =>
              actionTrigger === FillToBottomActionsEnum.resize
          )
        )
        .subscribe(() => this.resizeDebounced());
    }

    const optimizedResize = (function () {
      const callbacks = [];
      let running = false;

      // fired on resize event
      function resize() {
        if (!running) {
          running = true;

          if (window.requestAnimationFrame) {
            window.requestAnimationFrame(runCallbacks);
          } else {
            setTimeout(runCallbacks, 66);
          }
        }
      }

      // run the actual callbacks
      function runCallbacks() {
        callbacks.forEach(function (callback) {
          callback();
        });

        running = false;
      }

      // adds callback to loop
      function addCallback(callback) {
        if (callback) {
          callbacks.push(callback);
        }
      }

      return {
        // public method to add additional callback
        add: function (callback) {
          if (!callbacks.length) {
            window.addEventListener('resize', resize);
          }
          addCallback(callback);
        },
      };
    })();

    // start process
    optimizedResize.add(this.resizeDebounced.bind(this));
  }

  resize() {
    const boundingRect: ClientRect = this._el.getBoundingClientRect();

    const appHeight = Math.max(
      this._body.scrollHeight,
      this._body.offsetHeight,
      this._html.clientHeight,
      this._html.scrollHeight,
      this._html.offsetHeight
    );

    this._el.style.height = `${
      appHeight -
      (this._html.scrollTop + boundingRect.top) -
      (this.offsetFromBottom || 15)
    }px`;
  }

  resizeDebounced = debounce(() => {
    this.resize();
    this.onResize.emit();
  }, 200);

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
